/**
 * Better Auth Client Implementation
 * Following the official Better Auth documentation standards
 * Reference: https://www.better-auth.com/docs/basic-usage
 */

import { createAuthClient } from "better-auth/client"

// Get the auth service URL from environment or use default
const authBaseUrl = import.meta.env.VITE_AUTH_BASE_URL || 'http://localhost:3001'

// Clean the base URL to prevent double dots and ensure proper formatting
let cleanAuthBaseUrl = authBaseUrl
  .replace(/\/+$/, '') // Remove trailing slashes
  .replace(/\/auth$/, '') // Remove /auth suffix if present (Better Auth adds it automatically)
  .replace(/\/api\/auth$/, '') // Remove /api/auth suffix if present
  .replace(/\/api$/, '') // Remove /api suffix if present
  .replace(/\.+/g, '.') // Fix any double dots

// Additional validation and fallback for malformed URLs
if (cleanAuthBaseUrl.includes('..') || cleanAuthBaseUrl.startsWith('https://.')) {
  console.warn('⚠️ Detected malformed auth URL, using fallback')
  // Use production fallback if we're in production, otherwise localhost
  cleanAuthBaseUrl = import.meta.env.MODE === 'production' 
    ? 'https://auth.doc2plot.com'
    : 'http://localhost:3001'
}

// Debug logging for URL construction - ALWAYS log in production for debugging
console.log('🔧 Better Auth Client Configuration:')
console.log('  Original VITE_AUTH_BASE_URL:', authBaseUrl)
console.log('  Cleaned baseURL:', cleanAuthBaseUrl)
console.log('  Environment:', import.meta.env.MODE)
console.log('  All env vars:', {
  VITE_AUTH_BASE_URL: import.meta.env.VITE_AUTH_BASE_URL,
  VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
  MODE: import.meta.env.MODE
})

/**
 * Create the Better Auth client following official documentation
 * This is the standard way to interact with Better Auth
 */
export const authClient = createAuthClient({
  // Use the cleaned baseURL from environment variables
  baseURL: cleanAuthBaseUrl,
  // Use Better Auth's default basePath (/api/auth)
  // Explicitly set fetch options to ensure proper behavior
  fetchOptions: {
    credentials: 'include',
  },
})

// Debug: Log the actual client configuration - ALWAYS log for debugging
console.log('🔧 Better Auth Client Created:')
console.log('  baseURL (from env):', cleanAuthBaseUrl)
console.log('  basePath:', '/api/auth (Better Auth default)')
console.log('  Environment:', import.meta.env.MODE)
console.log('  Client instance:', authClient)

/**
 * Type exports for Better Auth
 */
export type Session = typeof authClient.$Infer.Session
export type User = typeof authClient.$Infer.Session.user

/**
 * Helper functions that match Better Auth patterns
 */

/**
 * Sign up a new user (Better Auth standard)
 * @example
 * const { data, error } = await signUpEmail({
 *   email: "user@example.com",
 *   password: "password123",
 *   name: "John Doe"
 * })
 */
export const signUpEmail = async (data: {
  email: string
  password: string
  name?: string
  callbackURL?: string
}) => {
  return authClient.signUp.email({
    email: data.email,
    password: data.password,
    name: data.name || '',
    callbackURL: data.callbackURL || "/dashboard"
  }, {
    onError: (ctx) => {
      console.error('Sign up error:', ctx.error.message)
    }
  })
}

/**
 * Sign in with email and password (Better Auth standard)
 * @example
 * const { data, error } = await signInEmail({
 *   email: "user@example.com",
 *   password: "password123"
 * })
 */
export const signInEmail = async (data: {
  email: string
  password: string
  rememberMe?: boolean
  callbackURL?: string
}) => {
  return authClient.signIn.email({
    email: data.email,
    password: data.password,
    callbackURL: data.callbackURL || "/dashboard",
    rememberMe: data.rememberMe !== false // Default true as per docs
  })
}

/**
 * Sign out the current user (Better Auth standard)
 */
export const signOut = async () => {
  return authClient.signOut({
    fetchOptions: {
      onSuccess: () => {
        // Clear any local state if needed
        window.location.href = "/login"
      }
    }
  })
}

// Throttle auth requests to prevent 429 errors
let lastAuthRequest = 0
const AUTH_REQUEST_THROTTLE = 2000 // 2 seconds between requests

/**
 * Get current session (Better Auth standard)
 * This is the proper way to check authentication status
 * Added throttling to prevent 429 rate limit errors
 */
export const getSession = async () => {
  const now = Date.now()
  const timeSinceLastRequest = now - lastAuthRequest
  
  if (timeSinceLastRequest < AUTH_REQUEST_THROTTLE) {
    console.log(`⏳ Throttling auth request, waiting ${AUTH_REQUEST_THROTTLE - timeSinceLastRequest}ms`)
    await new Promise(resolve => setTimeout(resolve, AUTH_REQUEST_THROTTLE - timeSinceLastRequest))
  }
  
  lastAuthRequest = Date.now()
  return authClient.getSession()
}

/**
 * Use session hook for reactive session management
 * This is the recommended way for client-side session access
 */
export const useSession = authClient.useSession

// Export the client for direct access when needed
export default authClient
